import { themeV2 as theme } from '@features/executive-report/domain/theme-v2';
import {
  infoIconGreen,
  pyarmidGradientBlue,
} from '@features/executive-report/images';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import { ShapeProps, Slide, TextProps } from '@features/powerpoint';
import { formatters, ValueInput } from '@utils/formattersV2';

const CARD_HEADER_FONT_SIZE = 18;
const KPI_FONT_SIZE = 36;

type PyramidData = {
  name: string;
  value: string;
};

const PYRAMID_LABEL_X_START = 6.31;
const PYRAMID_LABEL_Y_START = 3.55;
const PYRAMID_LABEL_X_OFFSET = 0.32;
const PYRAMID_LABEL_Y_OFFSET = 0.71;

const PYRAMID_VALUE_X = 9.58;
const PYRAMID_VALUE_Y_START = 3.55;

const PYRAMID_ARROW_X_START = 7.66;
const PYRAMID_ARROW_Y_START = 3.73;

const buildPyramidTexts = (data: PyramidData[]): TextProps[] => {
  return data.flatMap((d, index) => {
    const labelTextProps: TextProps = {
      text: d.name,
      options: {
        fontSize: 8,
        bold: true,
        align: 'center',
        valign: 'middle',
        h: 0.37,
        w: 1.37,
        x: PYRAMID_LABEL_X_START + index * PYRAMID_LABEL_X_OFFSET,
        y: PYRAMID_LABEL_Y_START + index * PYRAMID_LABEL_Y_OFFSET,
      },
    };
    const valueTextProps: TextProps = {
      text: d.value,
      options: {
        fontSize: 16,
        bold: true,
        color: theme.colors.inmarketGreen,
        valign: 'middle',
        h: 0.37,
        w: 3.26,
        x: PYRAMID_VALUE_X,
        y: PYRAMID_VALUE_Y_START + index * PYRAMID_LABEL_Y_OFFSET,
      },
    };
    return [labelTextProps, valueTextProps];
  });
};

const buildPyramidArrows = (pyramidData: PyramidData[]): ShapeProps[] => {
  return pyramidData.map(
    (_, index): ShapeProps => ({
      shapeName: 'line',
      options: {
        line: {
          color: theme.colors.inmarketGreen,
          width: 3,
          endArrowType: 'triangle',
        },
        h: 0,
        w: 1.56 - PYRAMID_LABEL_X_OFFSET * index,
        x: PYRAMID_ARROW_X_START + index * PYRAMID_LABEL_X_OFFSET,
        y: PYRAMID_ARROW_Y_START + index * PYRAMID_LABEL_Y_OFFSET,
      },
    })
  );
};

// must be a function due to bug reusing objects during presentation generation
const cardTextPropsOptions = (): TextProps['options'] => ({
  fill: { color: theme.colors.white, type: 'solid' },
  shadow: {
    type: 'outer',
    opacity: 0.09,
    blur: 4,
    angle: 90,
    offset: 2,
  },
});

type SlideProps = {
  navbarText: TextProps;
  brandObjective: string;
  cpiv: ValueInput;
  incrementalVisitLift: ValueInput;
  totalIncrementalVisits: ValueInput;
  totalProjectedVisits: ValueInput;
  uniqueUsers: ValueInput;
  impressions: ValueInput;
};

export const visitOverviewSlide = (props: SlideProps): Slide => {
  const pyramidData: PyramidData[] = [
    {
      name: 'INCREMENTAL VISITS',
      value: `${formatters.number(props.totalIncrementalVisits, { full: true })} (${formatters.lift(props.incrementalVisitLift)} Lift)`,
    },
    {
      name: 'TOTAL PROJECTED VISITS',
      value: formatters.number(props.totalProjectedVisits, { full: true }),
    },
    {
      name: 'UNIQUE USERS REACHED',
      value: formatters.number(props.uniqueUsers, { full: true }),
    },
    {
      name: 'TOTAL IMPRESSIONS',
      value: formatters.number(props.impressions, { full: true }),
    },
  ];
  return {
    slideMaster: slideMasters.light,
    color: theme.colors.metricBlue,
    images: [
      {
        path: pyarmidGradientBlue,
        h: 2.91,
        w: 2.69,
        x: 4.84,
        y: 3.29,
      },
      {
        path: infoIconGreen,
        h: 0.35,
        w: 0.35,
        x: 3.88,
        y: 6.63,
      },
    ],
    texts: [
      props.navbarText,
      // brand objective
      {
        text: [
          {
            text: 'BRAND OBJECTIVE',
            options: {
              fontSize: CARD_HEADER_FONT_SIZE,
              bold: true,
              breakLine: true,
            },
          },
          {
            text: props.brandObjective,
            options: {
              paraSpaceBefore: 12,
              paraSpaceAfter: 12,
              fontSize: 16,
            },
          },
        ],
        options: {
          ...cardTextPropsOptions(),
          h: 2.39,
          w: 3.42,
          x: 0.33,
          y: 0.89,
        },
      },
      // CPIV
      {
        text: [
          {
            text: 'CPIV',
            options: {
              fontSize: CARD_HEADER_FONT_SIZE,
              bold: true,
            },
          },
          {
            text: '(cost per incremental visit)',
            options: {
              fontSize: 14,
              bold: true,
              softBreakBefore: true,
              breakLine: true,
            },
          },
          {
            text: formatters.currency(props.cpiv, { full: true, decimals: 2 }),
            options: {
              paraSpaceBefore: 12,
              paraSpaceAfter: 12,
              fontSize: KPI_FONT_SIZE,
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
        ],
        options: {
          ...cardTextPropsOptions(),
          h: 1.81,
          w: 3.42,
          x: 0.33,
          y: 3.44,
        },
      },
      // Incremental Visit Lift
      {
        text: [
          {
            text: 'INCREMENTAL VISIT LIFT %',
            options: {
              fontSize: CARD_HEADER_FONT_SIZE,
              bold: true,
              breakLine: true,
            },
          },
          {
            text: formatWithSign(props.incrementalVisitLift, formatters.lift),
            options: {
              paraSpaceBefore: 12,
              paraSpaceAfter: 12,
              fontSize: KPI_FONT_SIZE,
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
        ],
        options: {
          ...cardTextPropsOptions(),
          h: 1.39,
          w: 3.86,
          x: 3.88,
          y: 0.89,
        },
      },
      // Total Incremental Visits
      {
        text: [
          {
            text: 'TOTAL ',
          },
          {
            text: 'INCREMENTAL VISITS',
            options: {
              fontSize: CARD_HEADER_FONT_SIZE,
              bold: true,
              breakLine: true,
            },
          },
          {
            text: formatters.number(props.totalIncrementalVisits, {
              full: true,
            }),
            options: {
              paraSpaceBefore: 12,
              paraSpaceAfter: 12,
              fontSize: KPI_FONT_SIZE,
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
        ],
        options: {
          ...cardTextPropsOptions(),
          h: 1.39,
          w: 3.98,
          x: 7.87,
          y: 0.89,
        },
      },
      // description
      {
        text: [
          {
            text: 'Foot traffic ',
            options: {
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
          {
            text: 'is measured by the number visits to specific, physical locations. Location visits are based on the number of observed visits after exposure to advertising.',
          },
        ],
        options: {
          fontSize: 12,
          h: 0.5,
          w: 7.98,
          x: 3.88,
          y: 2.45,
        },
      },
      // TODO: pyramid metrics
      ...buildPyramidTexts(pyramidData),
      // subtext
      {
        text: [
          {
            text: 'Who, what ',
            options: {
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
          {
            text: 'and ',
          },
          {
            text: 'where ',
            options: {
              color: theme.colors.inmarketGreen,
              bold: true,
            },
          },
          {
            text: 'are the three primary indicators to understand drivers of visit incrementality',
          },
        ],
        options: {
          fontSize: 11,
          h: 0.3,
          w: 8.63,
          x: 4.24,
          y: 6.66,
        },
      },
    ],
    shapes: buildPyramidArrows(pyramidData),
  };
};
